import React, { useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import Sidebar, { TopBar } from "./Sidebar";
import { Toaster } from "sonner";
import { Bell, BellOff, X } from "lucide-react";
import { useNotifications } from "../hooks/useNotifications";
import OfflineIndicator from "./OfflineIndicator";

function NotifPermissionBanner({ permission, requestPermission }) {
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem("lpg_notif_banner_dismissed") === "1"
  );
  if (permission === "granted" || dismissed) return null;
  if (typeof Notification === "undefined") return null;

  const isDenied = permission === "denied";
  const dismiss = () => {
    sessionStorage.setItem("lpg_notif_banner_dismissed", "1");
    setDismissed(true);
  };
  return (
    <div className={`px-4 py-2 text-xs flex items-center gap-3 border-b ${isDenied ? "bg-[#FEE2E2] border-[#FECACA] text-[#B91C1C]" : "bg-[#FEF3C7] border-[#FDE68A] text-[#B45309]"}`} data-testid="notif-banner">
      {isDenied ? <BellOff size={14} /> : <Bell size={14} />}
      <span className="flex-1">
        {isDenied
          ? "Notifications are blocked. Enable them in your browser site settings to receive chat, task and follow-up alerts."
          : "Turn on notifications to get pinged when you receive a chat, task or follow-up."}
      </span>
      {!isDenied && (
        <button
          onClick={requestPermission}
          className="h-7 px-3 rounded-sm bg-[#B45309] text-white font-bold hover:bg-[#92400E]"
          data-testid="notif-enable-btn"
        >Enable</button>
      )}
      <button onClick={dismiss} className="p-1 rounded-sm hover:bg-black/5" title="Dismiss" data-testid="notif-dismiss-btn">
        <X size={14} />
      </button>
    </div>
  );
}

const titles = {
  "/": "Dashboard",
  "/rate-card": "Rate Card",
  "/rate-analysis": "Rate Analysis",
  "/logs/sales": "Sale Logs",
  "/logs/purchases": "Purchase Logs",
  "/sale-calculator": "Sale Calculator",
  "/entry/purchase": "Gas Purchase Entry",
  "/entry/sale": "Gas Sale Entry",
  "/entry/transfer": "Godown to Godown Transfer",
  "/entry/adjustment": "Adjustment / Defective",
  "/entry/payment": "Payment Entry",
  "/expenses": "Expenses & Cash Holders",
  "/parties": "Customers & Vendors",
  "/pending/mt": "MT Entry Pending",
  "/pending/payments": "Pending Payments",
  "/pending/cylinders": "Pending Cylinders",
  "/challans": "Challan Series Tracker",
  "/tally": "Daily Tally",
  "/reports/daily-closing": "Daily Closing Stock",
  "/masters": "Masters",
  "/industrial/items": "Industrial Items",
  "/industrial/purchase": "Industrial Purchase Entry",
  "/industrial/sale": "Industrial Sale Entry",
  "/industrial/stock": "Industrial Stock & Transactions",
  "/audit": "Audit Logs",
  "/data-transfer": "Backup & Transfer",
};

export default function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const loc = useLocation();
  const { permission, requestPermission } = useNotifications();
  const title =
    titles[loc.pathname] ||
    (loc.pathname.startsWith("/parties/") ? "Party Ledger" : "LPG Manager");

  return (
    <div className="min-h-screen flex">
      <Sidebar mobileOpen={mobileOpen} onClose={() => setMobileOpen(false)} />
      <div className="flex-1 flex flex-col min-w-0">
        <TopBar onMenu={() => setMobileOpen(true)} title={title} />
        <NotifPermissionBanner permission={permission} requestPermission={requestPermission} />
        <main className="flex-1 p-4 sm:p-6 lg:p-8 bg-[#F9FAFB]">
          <Outlet />
        </main>
      </div>
      <Toaster position="top-right" richColors />
      <OfflineIndicator />
    </div>
  );
}
